import logoVariants from '@/assets/img/logo';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import authRoutes, { SIGNED_OUT_ROUTE_PREFIXES } from '@/constants/routes/auth';
import { useAuthContext } from '@/context/auth/useAuthContext';
import { Container, HStack, Image } from '@chakra-ui/react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import React from 'react';
import AuthHeader from './variants/AuthHeader';
import DefaultHeader from './variants/DefaultHeader';
import useScrollDirection from '@/hooks/useScrollDirection';

type Props = {
  variant?: 'transparent' | 'default';
  backgroundColor?: string;
  isFixed?: boolean;
  hideButtons?: boolean;
  centeredLogo?: boolean;
};

const Header = ({
  variant = 'default',
  backgroundColor = 'transparent',
  isFixed = false,
  hideButtons = false,
  centeredLogo = false,
}: Props) => {
  const { isAuthenticated, isMemberCare, isOnboarded } = useAuthContext();

  // The variant follows the route, not just the store. isAuthenticated flips as
  // soon as sign-in writes the user, which is a frame or two before the
  // navigation lands — long enough to show the signed-in header on the sign-in
  // page, which is what the "header renders wrong for a moment" reports were.
  const isOnSignedOutRoute = useRouterState({
    select: (state) => SIGNED_OUT_ROUTE_PREFIXES.some((prefix) => state.location.pathname.startsWith(prefix)),
  });
  const showSignedInHeader = isAuthenticated && !isOnSignedOutRoute;

  const isTransparent = variant === 'transparent';
  const defaultLogoPath = isTransparent ? logoVariants.white252x80 : logoVariants.purple252x80;
  // Gated on showSignedInHeader too: the member-care logo is signed-in-only UI and
  // would otherwise persist on a signed-out route during a teardown.
  const logoPath =
    isMemberCare && isOnboarded && showSignedInHeader ? logoVariants.purpleMemberCare229x40 : defaultLogoPath;

  const navigate = useNavigate();

  // Hide-on-scroll only applies to a fixed header. In normal flow the header
  // scrolls away on its own, and translating it produced a second, competing
  // movement on pages that were not even scrollable.
  const scrollDirection = useScrollDirection();
  const isVisible = !isFixed || scrollDirection === 'up';

  return (
    <Container
      as={'header'}
      transition="transform 0.3s ease-in-out"
      transform={isVisible ? 'translateY(0)' : 'translateY(-100%)'}
      maxWidth={MAX_CONTAINER_WIDTH}
      paddingX={0}
      top={0}
      zIndex={3}
      // `isFixed` renamed from `isSticky`, whose sense was inverted: it read as
      // "sticks in the flow" but `isSticky={false}` produced position:fixed.
      //
      // A fixed element resolves width against the viewport, so without left/right
      // and an auto margin it escaped the centred content column and pinned the
      // logo to the window edge on wide screens.
      position={isFixed ? 'fixed' : 'relative'}
      {...(isFixed ? { left: 0, right: 0, marginX: 'auto' } : {})}
      paddingTop={centeredLogo ? '40px' : '20px'}
      backgroundColor={backgroundColor}
    >
      <HStack justifyContent={centeredLogo ? 'center' : 'space-between'} alignItems={'center'}>
        <Image
          src={logoPath}
          alt="my-omnia"
          _hover={{ cursor: 'pointer' }}
          // width={'126px'}
          height={'40px'}
          onClick={() => {
            navigate({ to: showSignedInHeader ? '/' : authRoutes.signIn });
          }}
        />
        {!hideButtons && (
          <HStack alignItems={'center'}>
            {!showSignedInHeader && <AuthHeader />}
            {showSignedInHeader && <DefaultHeader isTransparent={isTransparent} />}
          </HStack>
        )}
      </HStack>
    </Container>
  );
};

export default Header;
